import { WebAuth } from 'auth0-js'
import { persistor } from '../store'

import { AuthResponse } from '../components/Login/types'
import { FEATURES_PERSIST_KEY } from '../store/reducerPersistors/constants'

class Auth {
  auth0: WebAuth
  tokenRenewalTimeout: NodeJS.Timeout | undefined

  constructor() {
    this.auth0 = new WebAuth({
      audience: import.meta.env.REACT_APP_AUTH_AUDIENCE,
      domain: import.meta.env.REACT_APP_AUTH_DOMAIN || '',
      clientID: import.meta.env.REACT_APP_AUTH_CLIENT_ID || '',

      scope: 'openid email profile',
      responseType: 'token id_token',
      redirectUri: `${window.location.origin}/login`
    })

    this.tokenRenewalTimeout = undefined

    this.login = this.login.bind(this)
    this.logout = this.logout.bind(this)
    this.scheduleTokenRenewal = this.scheduleTokenRenewal.bind(this)
    this.handleAuthentication = this.handleAuthentication.bind(this)
  }

  checkSession(): Promise<AuthResponse> {
    return new Promise((resolve, reject) => {
      this.auth0.checkSession({}, (err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          resolve(authResult)
        } else {
          reject(err)
        }
      })
    })
  }

  scheduleTokenRenewal(
    expiresIn: number | undefined,
    callback: (authResponse: AuthResponse) => Promise<void>
  ): void {
    const timeout = ((expiresIn || 0) - 60) * 1000

    if (timeout <= 0) {
      return
    }

    if (this.tokenRenewalTimeout) {
      return
    }

    this.tokenRenewalTimeout = setTimeout(async () => {
      delete this.tokenRenewalTimeout
      const authResult = await this.checkSession()

      if (!authResult) {
        return
      }

      this.scheduleTokenRenewal(authResult.expiresIn, callback)
      callback(authResult)
    }, timeout)
  }

  handleAuthentication(hash: string): Promise<AuthResponse> {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash({ hash }, (err, authResponse) => {
        window.location.hash = ''

        if (err || !authResponse || !authResponse.accessToken) {
          reject(err)
        } else {
          resolve(authResponse)
        }
      })
    })
  }

  login(referrer: string) {
    this.auth0.authorize({ state: referrer })
  }

  async logout() {
    await persistor.purgeExceptForKeys([FEATURES_PERSIST_KEY])

    this.auth0.logout({
      returnTo: window.location.origin
    })

    if (!this.tokenRenewalTimeout) {
      return
    }

    clearTimeout(this.tokenRenewalTimeout)
  }
}

export default new Auth()
